import { z } from 'zod';
import { TarefaPedido } from './pedido.js';
import { AjusteNaoUtil, MotivoDispensa, MotivoEspera, SituacaoRegistro, TarefaStatus } from './enums.js';
import { BoolQuery, CompetenciaChave, CompetenciaChaveQuery, DataISO, Id, Paginacao } from './comum.js';
import { Anexo, Apontamento, ChecklistItem, Comentario, Entrega } from './detalhe.js';
import { TarefaProcesso } from './fluxo.js';
import { Entregaveis } from './entregaveis.js';
import { ResumoEntregaveis } from './documentos-tarefa.js';
import { EnvioDaTarefa } from './remessa.js';

// ---- vínculo cliente × obrigação ----
export const VinculoBody = z.object({
  obrigacaoId: Id,
  ativo: z.boolean().default(true),
  competenciaInicio: CompetenciaChave,
  competenciaFim: CompetenciaChave.nullable().default(null),
  gerarRetroativoDesde: CompetenciaChave.nullable().default(null),
  responsavelMembroId: Id.nullable().default(null),
  parametros: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])).default({}),
  observacao: z.string().trim().max(300).nullable().default(null),
});
export type VinculoBody = z.infer<typeof VinculoBody>;

export const Vinculo = VinculoBody.extend({
  id: Id,
  clienteId: Id,
  obrigacaoSigla: z.string(),
  obrigacaoNome: z.string(),
  tarefasAbertas: z.number().int(),
  /** chave do parâmetro que a regra exige no vínculo (ex.: diaVencimentoIss), ou null */
  parametroChave: z.string().nullable(),
});
export type Vinculo = z.infer<typeof Vinculo>;

/** Marcação em massa: liga/desliga uma obrigação para vários clientes. */
export const VinculoLoteBody = z.object({
  obrigacaoId: Id,
  clienteIds: z.array(Id).min(1).max(2000),
  ligar: z.boolean(),
  competenciaInicio: CompetenciaChave.optional(),
  gerarRetroativoDesde: CompetenciaChave.nullable().optional(),
  parametros: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])).optional(),
});
export type VinculoLoteBody = z.infer<typeof VinculoLoteBody>;
/** Copia as obrigações ativas de outro cliente (as que este ainda não tem), com os mesmos parâmetros. */
export const CopiarVinculosBody = z.object({ deClienteId: Id, competenciaInicio: CompetenciaChave.optional(), gerarRetroativoDesde: CompetenciaChave.nullable().optional() });
export type CopiarVinculosBody = z.infer<typeof CopiarVinculosBody>;

/** Sugestão de vínculos por regime (onboarding em massa). */
export const SugestaoQuery = z.object({ clienteIds: z.array(Id).max(2000).optional(), departamento: z.string().trim().max(8).optional() });
export const Sugestao = z.object({
  clientes: z.array(z.object({ clienteId: Id, nome: z.string(), documento: z.string(), regime: z.string().nullable(), obrigacaoIds: z.array(Id) })),
  obrigacoes: z.array(z.object({ id: Id, sigla: z.string(), nome: z.string(), departamentoSugerido: z.string().nullable(), regimesAplicaveis: z.array(z.string()), parametroChave: z.string().nullable() })),
  totalVinculos: z.number().int(),
});
export type Sugestao = z.infer<typeof Sugestao>;
export const AplicarSugestaoBody = z.object({
  itens: z.array(z.object({ clienteId: Id, obrigacaoIds: z.array(Id).min(1) })).min(1).max(2000),
  competenciaInicio: CompetenciaChave.optional(),
  gerarRetroativoDesde: CompetenciaChave.nullable().optional(),
  /** parâmetros por obrigação (ex.: { [issId]: { diaVencimentoIss: 10 } }) */
  parametros: z.record(Id, z.record(z.string(), z.union([z.string(), z.number()]))).optional(),
});
export type AplicarSugestaoBody = z.infer<typeof AplicarSugestaoBody>;

/** Célula da matriz de marcação. */
export const MarcacaoMatriz = z.object({
  clientes: z.array(z.object({ id: Id, razaoSocial: z.string(), documento: z.string(), regimeAtual: z.string().nullable(), uf: z.string() })),
  obrigacoes: z.array(z.object({ id: Id, sigla: z.string(), nome: z.string(), esfera: z.string(), regimesAplicaveis: z.array(z.string()), departamentoSugerido: z.string().nullable(), parametroChave: z.string().nullable() })),
  /** clienteId → obrigacaoId[] ativos */
  vinculos: z.record(z.string(), z.array(z.string())),
});
export type MarcacaoMatriz = z.infer<typeof MarcacaoMatriz>;

// ---- tarefa ----
export const Tarefa = z.object({
  /** parcelamento ou recorrência com fim: "3/12" */
  parcela: z.string().nullable().optional(),
  /** ocorrência de uma tarefa que se repete */
  recorrenciaId: Id.nullable().optional(),
  /** "todo mês", "a cada 3 meses"… */
  recorrenciaRotulo: z.string().nullable().optional(),
  id: Id,
  clienteId: Id.nullable(),
  clienteNome: z.string().nullable(),
  obrigacaoId: Id.nullable(),
  obrigacaoSigla: z.string().nullable(),
  /** resumo das guias para as visões coletivas (lista, quadro) */
  guias: z.object({ esperadas: z.number().int(), registradas: z.number().int(), pagas: z.number().int(), vencidas: z.number().int(), faltam: z.number().int() }).nullable().optional(),
  /** sigla: da obrigação, ou da própria tarefa quando avulsa/etapa */
  departamento: z.string().nullable(),
  titulo: z.string(),
  competenciaChave: CompetenciaChave.nullable(),
  competenciaLabel: z.string().nullable(),
  parcelaNumero: z.number().int(),
  vencimentoLegal: DataISO,
  vencimentoProrrogado: DataISO.nullable(),
  vencimentoManual: DataISO.nullable(),
  vencimentoEfetivo: DataISO,
  dataLimiteInterna: DataISO,
  dataAbertura: DataISO,
  status: TarefaStatus,
  situacaoRegistro: SituacaoRegistro,
  responsavelMembroId: Id.nullable(),
  responsavelApelido: z.string().nullable(),
  atrasada: z.boolean(),
  diasParaVencer: z.number().int(),
  /** aberta, o prazo interno já passou e o vencimento real ainda não: pede atenção, não é atraso */
  internoVencido: z.boolean().optional(),
  /** dias até o prazo interno (negativo = passou) */
  diasInterno: z.number().int().optional(),
  temInteracao: z.boolean(),
  revisaoPendente: z.boolean(),
  /** o vínculo/regime não prevê mais esta competência; a tarefa tem trabalho e ficou para a equipe decidir */
  foraDoPlano: z.boolean().optional(),
  motivoEspera: MotivoEspera.nullable(),
  motivoDispensa: MotivoDispensa.nullable(),
  observacao: z.string().nullable(),
  concluidaEm: z.string().nullable(),
  concluidaComAtraso: z.boolean().nullable(),
  /** funcionário do cliente a que a tarefa se refere */
  funcionarioId: Id.nullable().optional(),
  funcionarioNome: z.string().nullable().optional(),
});
export type Tarefa = z.infer<typeof Tarefa>;

export const TarefasQuery = Paginacao.extend({
  de: DataISO.optional(),
  ate: DataISO.optional(),
  clienteId: Id.optional(),
  funcionarioId: Id.optional(),
  obrigacaoId: Id.optional(),
  responsavelMembroId: Id.optional(),
  status: z.union([TarefaStatus, z.array(TarefaStatus)]).optional(),
  somenteAbertas: BoolQuery.default(false),
  somenteAtrasadas: BoolQuery.default(false),
  /** abertas com o prazo interno vencido e o vencimento real ainda por vir */
  internoVencido: BoolQuery.default(false),
  competenciaChave: CompetenciaChaveQuery.optional(),
  /** sigla do departamento sugerido pela obrigação (FIS, DP…) */
  departamento: z.string().trim().max(8).optional(),
  /** trecho da razão social / nome fantasia do cliente ou do título */
  busca: z.string().trim().max(120).optional(),
  /** só as do membro logado */
  minhas: BoolQuery.default(false),
  semResponsavel: BoolQuery.default(false),
  ordem: z.enum(['vencimento', 'cliente', 'obrigacao']).default('vencimento'),
  /** gestão: faixa de competências (AAAAMM) */
  competenciaDe: CompetenciaChaveQuery.optional(),
  competenciaAte: CompetenciaChaveQuery.optional(),
  /** gestão: de onde a tarefa veio */
  origem: z.enum(['RECONCILIADOR', 'MANUAL', 'FLUXO']).optional(),
  /** gestão: ATIVA (padrão), FORA_DO_PLANO (o vínculo/regime não prevê mais, mas tem trabalho) ou TODAS */
  situacao: z.enum(['ATIVA', 'FORA_DO_PLANO', 'TODAS']).optional(),
  /** gestão: só as sem trabalho registrado (as que saem sem forçar) */
  semTrabalho: BoolQuery.default(false),
  /** painel (fatia 2a): tarefas com guia cujo pagamento o cliente avisou nos últimos 7 dias — qualquer status */
  pagamentoAvisado: BoolQuery.default(false),
  /** painel (fatia 2a): tarefas com guia vencida de que o cliente pediu a atualizada — qualquer status */
  atualizadaPedida: BoolQuery.default(false),
});
export type TarefasQuery = z.infer<typeof TarefasQuery>;

export const TarefasPagina = z.object({
  itens: z.array(Tarefa),
  total: z.number().int(),
  page: z.number().int(),
  perPage: z.number().int(),
  resumo: z.object({
    abertas: z.number().int(), atrasadas: z.number().int(), hoje: z.number().int(), semana: z.number().int(), concluidas: z.number().int(), internoVencido: z.number().int().optional(),
  }),
});
export type TarefasPagina = z.infer<typeof TarefasPagina>;

/** Gerar tarefas de competências passadas para vínculos existentes (controle em lote). */
export const GerarTarefasBody = z.object({
  clienteId: Id.optional(), obrigacaoId: Id.optional(), desde: CompetenciaChave, recuarRegime: z.boolean().default(true),
  /** o 'gerar retroativo desde' dos vínculos passa a ser exatamente `desde` (as competências anteriores sem trabalho saem) — sem isto, regerar volta ao período antigo */
  redefinir: z.boolean().default(false),
  /** até quantos dias à frente abrir tarefas (padrão 90) */
  horizonteDias: z.number().int().min(1).max(400).optional(),
});
export type GerarTarefasBody = z.infer<typeof GerarTarefasBody>;
export const GerarTarefasResultado = z.object({ vinculos: z.number().int(), criadas: z.number().int(), reativadas: z.number().int(), regimesRecuados: z.number().int(), avisos: z.array(z.string()) });

export const ReconciliacaoResultado = z.object({
  /** tarefas 'canceladas sem efeito' que voltaram ao plano (vínculo/regime religado) */
  reativadas: z.number().int().optional(),
  vinculos: z.number().int(), criadas: z.number().int(), atualizadas: z.number().int(), divergentes: z.number().int(),
  canceladas: z.number().int(), removidas: z.number().int(), duracaoMs: z.number().int(), avisos: z.array(z.string()),
});
export type ReconciliacaoResultado = z.infer<typeof ReconciliacaoResultado>;

// ---- F4: detalhe, ações e visões ----
export const TarefaEvento = z.object({
  id: z.string(),
  tipo: z.string(),
  membroId: Id.nullable(),
  membroApelido: z.string().nullable(),
  de: z.string().nullable(),
  para: z.string().nullable(),
  descricao: z.string().nullable(),
  quando: z.string(),
});
export type TarefaEvento = z.infer<typeof TarefaEvento>;

export const TarefaDetalhe = Tarefa.extend({
  obrigacaoNome: z.string().nullable(),
  /** código do catálogo (DAS, DCTFWEB…) — para ações do Integra Contador */
  obrigacaoCodigo: z.string().nullable().optional(),
  clienteDocumento: z.string().nullable(),
  clienteUf: z.string().nullable(),
  departamento: z.string().nullable(),
  origem: z.enum(['RECONCILIADOR', 'MANUAL', 'FLUXO']),
  exigeRevisao: z.boolean(),
  requerEntrega: z.string(),
  revisorMembroId: Id.nullable(),
  reaberturas: z.number().int(),
  iniciadaEm: z.string().nullable(),
  aguardandoDesde: z.string().nullable(),
  concluidaPorApelido: z.string().nullable(),
  /** status para os quais o membro logado pode levar a tarefa agora */
  transicoesPossiveis: z.array(TarefaStatus),
  /** por que não dá para concluir agora (entrega faltando, checklist obrigatório aberto) — null = pode */
  bloqueioConclusao: z.string().nullable(),
  /** o que esta tarefa gera e o que já chegou em cada vaga */
  entregaveis: ResumoEntregaveis.nullable().optional(),
  eventos: z.array(TarefaEvento),
  checklist: z.array(ChecklistItem),
  entregas: z.array(Entrega),
  anexos: z.array(Anexo),
  comentarios: z.array(Comentario),
  apontamentos: z.array(Apontamento),
  minutosTotais: z.number().int(),
  /** cronômetro do membro logado rodando NESTA tarefa */
  cronometroDesde: z.string().nullable(),
  /** presente quando a tarefa é a etapa de um processo (workflow) */
  processo: TarefaProcesso.nullable(),
  pedido: TarefaPedido.nullable().optional(),
  /** o envio ao cliente visto desta tarefa (pendentes, envio aberto, último envio) */
  envio: EnvioDaTarefa.nullable().optional(),
});
export type TarefaDetalhe = z.infer<typeof TarefaDetalhe>;

export const TransicaoBody = z.object({
  para: TarefaStatus,
  motivoEspera: MotivoEspera.nullable().optional(),
  motivoDispensa: MotivoDispensa.nullable().optional(),
  motivo: z.string().trim().max(300).nullable().optional(),
  revisorMembroId: Id.nullable().optional(),
  /** concluir com data retroativa (fez ontem, marcou hoje); nunca futura. Vazio = agora */
  concluidaEm: DataISO.nullable().optional(),
});
export type TransicaoBody = z.infer<typeof TransicaoBody>;

export const AtribuirBody = z.object({ responsavelMembroId: Id.nullable() });
export type AtribuirBody = z.infer<typeof AtribuirBody>;

export const VencimentoManualBody = z.object({ vencimentoManual: DataISO.nullable(), motivo: z.string().trim().max(300).nullable().optional() });
export type VencimentoManualBody = z.infer<typeof VencimentoManualBody>;

export const TarefaEditarBody = z.object({
  titulo: z.string().trim().min(2).max(200).optional(),
  observacao: z.string().trim().max(2000).nullable().optional(),
  departamentoSigla: z.string().trim().max(8).nullable().optional(),
  /** funcionário do cliente (null = tira) */
  funcionarioId: Id.nullable().optional(),
});
export type TarefaEditarBody = z.infer<typeof TarefaEditarBody>;

/** Tarefa avulsa (não vem de obrigação): demanda interna, pedido do cliente… */
export const TarefaAvulsaBody = z.object({
  titulo: z.string().trim().min(2).max(200),
  clienteId: Id.nullable().default(null),
  /** funcionário do cliente a que a tarefa se refere */
  funcionarioId: Id.nullable().optional(),
  vencimento: DataISO,
  dataLimiteInterna: DataISO.nullable().default(null),
  responsavelMembroId: Id.nullable().default(null),
  departamentoSigla: z.string().trim().max(8).nullable().default(null),
  observacao: z.string().trim().max(2000).nullable().default(null),
  /** modelo de tarefa escolhido no seletor: traz o checklist */
  modeloId: Id.nullable().optional(),
  /** sem `dataLimiteInterna`: prazo interno = N dias úteis antes do vencimento */
  antecedenciaDiasUteis: z.number().int().min(0).max(60).optional(),
});
export type TarefaAvulsaBody = z.infer<typeof TarefaAvulsaBody>;

export const TarefaLoteBody = z.discriminatedUnion('acao', [
  z.object({ acao: z.literal('atribuir'), ids: z.array(Id).min(1).max(500), responsavelMembroId: Id.nullable() }),
  z.object({ acao: z.literal('status'), ids: z.array(Id).min(1).max(500), transicao: TransicaoBody }),
  /** apaga tarefas sem trabalho registrado (supervisor); `forcar` apaga mesmo com trabalho — os anexos passam ao cliente */
  z.object({ acao: z.literal('excluir'), ids: z.array(Id).min(1).max(500), forcar: z.boolean().default(false) }),
]);
export type TarefaLoteBody = z.infer<typeof TarefaLoteBody>;

export const TarefaLoteResultado = z.object({ ok: z.number().int(), erros: z.array(z.object({ id: Id, erro: z.string() })) });
export type TarefaLoteResultado = z.infer<typeof TarefaLoteResultado>;

export const MatrizQuery = z.object({
  eixo: z.enum(['VENCIMENTO', 'COMPETENCIA']).default('VENCIMENTO'),
  de: DataISO.optional(),
  ate: DataISO.optional(),
  competenciaChave: CompetenciaChaveQuery.optional(),
  departamento: z.string().trim().max(8).optional(),
  responsavelMembroId: Id.optional(),
  status: z.union([TarefaStatus, z.array(TarefaStatus)]).optional(),
  busca: z.string().trim().max(120).optional(),
  page: z.coerce.number().int().min(1).default(1),
  perPage: z.coerce.number().int().min(10).max(200).default(50),
  ordem: z.enum(['atraso', 'cliente']).default('atraso'),
});
export type MatrizQuery = z.infer<typeof MatrizQuery>;

/** Célula compacta da matriz (chaves curtas: são milhares por tela). */
export const MatrizCelula = z.object({
  t: Id, s: TarefaStatus, v: DataISO, r: z.string().nullable(), p: z.number().int(), a: z.boolean(), d: z.boolean(),
});
export type MatrizCelula = z.infer<typeof MatrizCelula>;

export const MatrizResposta = z.object({
  periodo: z.object({ eixo: z.enum(['VENCIMENTO', 'COMPETENCIA']), de: DataISO.nullable(), ate: DataISO.nullable(), competenciaChave: CompetenciaChave.nullable() }),
  colunas: z.array(z.object({ obrigacaoId: Id, sigla: z.string(), nome: z.string(), departamento: z.string().nullable() })),
  linhas: z.array(z.object({
    clienteId: Id, nome: z.string(), documento: z.string(), regime: z.string().nullable(), atrasadas: z.number().int(), abertas: z.number().int(),
    celulas: z.record(z.string(), z.array(MatrizCelula)),
  })),
  resumo: z.object({ porStatus: z.record(z.string(), z.number().int()), totalCelulas: z.number().int(), atrasadas: z.number().int() }),
  paginacao: z.object({ page: z.number().int(), perPage: z.number().int(), total: z.number().int() }),
});
export type MatrizResposta = z.infer<typeof MatrizResposta>;

export const PainelResposta = z.object({
  resumo: z.object({ pedidosAbertos: z.number().int().optional(), pedidosAtrasados: z.number().int().optional(), abertas: z.number().int(), atrasadas: z.number().int(), hoje: z.number().int(), semana: z.number().int(), concluidas: z.number().int(), noPrazo: z.number().int(), divergentes: z.number().int(), semResponsavel: z.number().int(), internoVencido: z.number().int().optional(), vencimentosVencidos: z.number().int().optional(), vencimentosAVencer: z.number().int().optional() }),
  porDepartamento: z.array(z.object({ sigla: z.string(), nome: z.string(), cor: z.string().nullable(), abertas: z.number().int(), atrasadas: z.number().int(), hoje: z.number().int(), semana: z.number().int() })),
  porResponsavel: z.array(z.object({ membroId: Id.nullable(), apelido: z.string(), nome: z.string(), abertas: z.number().int(), atrasadas: z.number().int(), hoje: z.number().int(), semana: z.number().int() })),
  clientesAtrasados: z.array(z.object({ clienteId: Id, nome: z.string(), atrasadas: z.number().int(), maisAntiga: DataISO })),
  proximas: z.array(Tarefa),
  /** a fila de quem está logado: as abertas dele, pela urgência */
  minhaFila: z.object({ abertas: z.number().int(), atrasadas: z.number().int(), hoje: z.number().int(), itens: z.array(Tarefa) }).optional(),
  /** o que veio dos clientes (ou saiu do plano) e ainda espera alguém */
  atencao: z.object({
    mensagens: z.array(z.object({ tarefaId: Id, clienteNome: z.string(), titulo: z.string(), quando: z.string(), trecho: z.string() })),
    solicitacoesRespondidas: z.number().int(), arquivosDoCliente: z.number().int(), foraDoPlano: z.number().int(), clientesSemObrigacao: z.number().int(),
    /** e-mails que não saíram nos últimos 7 dias e clientes baixados além da retenção (LGPD) */
    emailsComFalha: z.number().int().optional(), retencaoCandidatos: z.number().int().optional(),
    /** envios ao cliente que não saíram nos últimos 7 dias (cliente sem contato, arquivos tirados do portal) e envios por e-mail sem nenhuma abertura há mais de 3 dias */
    enviosFalhos: z.number().int().optional(), enviosSemAbertura: z.number().int().optional(),
    /** fatia 2a: clientes avisaram pagamento nos últimos 7 dias (informativo); clientes pediram guia atualizada e ninguém respondeu */
    pagamentosAvisados: z.number().int().optional(), atualizadasPedidas: z.number().int().optional(),
    /** fatia 2c: envios que esperam a mensagem do WhatsApp (contatos que preferem WhatsApp); itens de envio que não chegaram a ninguém nos últimos 7 dias */
    whatsappPendentes: z.number().int().optional(), itensSemDestinatario: z.number().int().optional(),
    /** fatia 3a: PDFs na Caixa de entrada de guias esperando alguém resolver */
    caixaGuias: z.number().int().optional(),
  }).optional(),
  /** quantos membros ativos tem o escritório (com um só, 'sem responsável' não é aviso) */
  membrosAtivos: z.number().int().optional(),
  /** departamento pessoal: itens do painel do pessoal e quantos são urgentes */
  pessoal: z.object({ itens: z.number().int(), urgentes: z.number().int() }).optional(),
  /** correspondências do e-CAC ainda não tratadas (Integra Contador) */
  caixaPostal: z.object({ ativa: z.boolean(), abertas: z.number().int(), relevantes: z.number().int(), itens: z.array(z.object({ id: Id, clienteId: Id, clienteNome: z.string(), assunto: z.string(), dataEnvio: z.string().nullable(), relevante: z.boolean(), origem: z.string().nullable(), temCorpo: z.boolean() })) }).optional(),
});
export type PainelResposta = z.infer<typeof PainelResposta>;

export const CalendarioQuery = z.object({ ano: z.coerce.number().int().min(2000).max(2100), mes: z.coerce.number().int().min(1).max(12), departamento: z.string().trim().max(8).optional(), responsavelMembroId: Id.optional(), minhas: BoolQuery.default(false) });
export type CalendarioQuery = z.infer<typeof CalendarioQuery>;

export const CalendarioResposta = z.object({
  dias: z.array(z.object({ data: DataISO, abertas: z.number().int(), atrasadas: z.number().int(), concluidas: z.number().int(), siglas: z.array(z.string()) })),
});
export type CalendarioResposta = z.infer<typeof CalendarioResposta>;

// ---- parcelamentos de tributos (entidade própria: o cliente pode ter vários)
export const OrgaoParcelamento = z.enum(['RFB', 'PGFN', 'SIMPLES', 'INSS', 'ESTADUAL', 'MUNICIPAL', 'OUTRO']);
export type OrgaoParcelamento = z.infer<typeof OrgaoParcelamento>;
export const SituacaoParcelamento = z.enum(['ATIVO', 'QUITADO', 'RESCINDIDO']);
export type SituacaoParcelamento = z.infer<typeof SituacaoParcelamento>;

export const ParcelamentoBody = z.object({
  clienteId: Id,
  orgao: OrgaoParcelamento,
  numero: z.string().trim().max(40).nullable().default(null),
  descricao: z.string().trim().max(160).nullable().default(null),
  parcelas: z.number().int().min(1).max(240),
  primeiraCompetencia: CompetenciaChave,
  /** nulo = último dia útil do mês (padrão da RFB/PGFN e do Simples) */
  diaVencimento: z.number().int().min(1).max(31).nullable().default(null),
  /** prazo interno: dias úteis antes do vencimento */
  antecedenciaDiasUteis: z.number().int().min(0).max(60).default(2),
  /** a tarefa abre tantos dias corridos antes do vencimento */
  aberturaDiasAntes: z.number().int().min(0).max(180).default(10),
  valorParcela: z.number().min(0).nullable().default(null),
  valorTotal: z.number().min(0).nullable().default(null),
  departamentoSigla: z.string().trim().max(8).nullable().default(null),
  responsavelMembroId: Id.nullable().default(null),
  observacao: z.string().trim().max(300).nullable().default(null),
});
export type ParcelamentoBody = z.infer<typeof ParcelamentoBody>;

export const Parcelamento = z.object({
  id: Id, clienteId: Id, clienteNome: z.string().nullable(), orgao: OrgaoParcelamento, rotuloOrgao: z.string(),
  numero: z.string().nullable(), descricao: z.string().nullable(), parcelas: z.number().int(),
  primeiraCompetencia: CompetenciaChave, ultimaCompetencia: CompetenciaChave, diaVencimento: z.number().int().nullable(),
  antecedenciaDiasUteis: z.number().int(), aberturaDiasAntes: z.number().int(),
  valorParcela: z.number().nullable(), valorTotal: z.number().nullable(), departamentoSigla: z.string().nullable(),
  responsavelMembroId: Id.nullable(), responsavelApelido: z.string().nullable(), situacao: SituacaoParcelamento,
  encerradoEm: z.string().nullable(), observacao: z.string().nullable(), criadoEm: z.string(),
  pagas: z.number().int(), abertas: z.number().int(), atrasadas: z.number().int(), proximoVencimento: DataISO.nullable(),
});
export type Parcelamento = z.infer<typeof Parcelamento>;
/** Edição: só o que vier no corpo é alterado (sem defaults, senão o PATCH apagaria campos). */
export const ParcelamentoEditarBody = z.object({
  orgao: OrgaoParcelamento.optional(),
  numero: z.string().trim().max(40).nullable().optional(),
  descricao: z.string().trim().max(160).nullable().optional(),
  parcelas: z.number().int().min(1).max(240).optional(),
  primeiraCompetencia: CompetenciaChave.optional(),
  diaVencimento: z.number().int().min(1).max(31).nullable().optional(),
  antecedenciaDiasUteis: z.number().int().min(0).max(60).optional(),
  aberturaDiasAntes: z.number().int().min(0).max(180).optional(),
  valorParcela: z.number().min(0).nullable().optional(),
  valorTotal: z.number().min(0).nullable().optional(),
  departamentoSigla: z.string().trim().max(8).nullable().optional(),
  responsavelMembroId: Id.nullable().optional(),
  observacao: z.string().trim().max(300).nullable().optional(),
});
export type ParcelamentoEditarBody = z.infer<typeof ParcelamentoEditarBody>;
export const ParcelamentoCriado = z.object({ parcelamento: Parcelamento, tarefas: z.number().int() });
export const ParcelamentoAtualizado = z.object({ parcelamento: Parcelamento, criadas: z.number().int(), removidas: z.number().int() });
export const ParcelamentoEncerrarBody = z.object({ situacao: SituacaoParcelamento });

// ---- tarefas avulsas que se repetem (renovação, licença, certificado, serviço periódico)
export const UnidadeRecorrencia = z.enum(['SEMANA', 'MES', 'ANO']);
export type UnidadeRecorrencia = z.infer<typeof UnidadeRecorrencia>;
export const SituacaoRecorrencia = z.enum(['ATIVA', 'ENCERRADA']);
export type SituacaoRecorrencia = z.infer<typeof SituacaoRecorrencia>;

export const RecorrenciaBody = z.object({
  /** nulo = tarefa interna do escritório */
  clienteId: Id.nullable().default(null),
  titulo: z.string().trim().min(2).max(180),
  /** repete a cada `intervalo` `unidade`(s) */
  intervalo: z.number().int().min(1).max(60).default(1),
  unidade: UnidadeRecorrencia.default('MES'),
  primeiroVencimento: DataISO,
  /** nulo = sem data para acabar */
  ocorrencias: z.number().int().min(1).max(240).nullable().default(null),
  /** vencimento em dia não útil */
  ajuste: AjusteNaoUtil.default('ANTECIPAR'),
  antecedenciaDiasUteis: z.number().int().min(0).max(60).default(0),
  aberturaDiasAntes: z.number().int().min(0).max(365).default(10),
  departamentoSigla: z.string().trim().max(8).nullable().default(null),
  responsavelMembroId: Id.nullable().default(null),
  observacao: z.string().trim().max(2000).nullable().default(null),
  /** modelo de tarefa escolhido no seletor: dá o checklist de cada ocorrência */
  modeloId: Id.nullable().default(null),
});
export type RecorrenciaBody = z.infer<typeof RecorrenciaBody>;

/** Edição: só o que vier no corpo é alterado (sem defaults, senão o PATCH apagaria campos). */
export const RecorrenciaEditarBody = z.object({
  titulo: z.string().trim().min(2).max(180).optional(),
  intervalo: z.number().int().min(1).max(60).optional(),
  unidade: UnidadeRecorrencia.optional(),
  primeiroVencimento: DataISO.optional(),
  ocorrencias: z.number().int().min(1).max(240).nullable().optional(),
  ajuste: AjusteNaoUtil.optional(),
  antecedenciaDiasUteis: z.number().int().min(0).max(60).optional(),
  aberturaDiasAntes: z.number().int().min(0).max(365).optional(),
  departamentoSigla: z.string().trim().max(8).nullable().optional(),
  responsavelMembroId: Id.nullable().optional(),
  observacao: z.string().trim().max(2000).nullable().optional(),
});
export type RecorrenciaEditarBody = z.infer<typeof RecorrenciaEditarBody>;

export const Recorrencia = z.object({
  id: Id, clienteId: Id.nullable(), clienteNome: z.string().nullable(), titulo: z.string(),
  intervalo: z.number().int(), unidade: UnidadeRecorrencia, rotulo: z.string(), primeiroVencimento: DataISO, ocorrencias: z.number().int().nullable(),
  ajuste: AjusteNaoUtil, antecedenciaDiasUteis: z.number().int(), aberturaDiasAntes: z.number().int(),
  departamentoSigla: z.string().nullable(), responsavelMembroId: Id.nullable(), responsavelApelido: z.string().nullable(),
  situacao: SituacaoRecorrencia, encerradaEm: z.string().nullable(), observacao: z.string().nullable(), criadoEm: z.string(),
  geradas: z.number().int(), concluidas: z.number().int(), abertas: z.number().int(), atrasadas: z.number().int(), proximoVencimento: DataISO.nullable(),
});
export type Recorrencia = z.infer<typeof Recorrencia>;
export const RecorrenciaCriada = z.object({ recorrencia: Recorrencia, tarefas: z.number().int(), primeiraTarefaId: Id.nullable() });
export const RecorrenciaAtualizada = z.object({ recorrencia: Recorrencia, criadas: z.number().int(), removidas: z.number().int() });
export const RecorrenciaSituacaoBody = z.object({ situacao: SituacaoRecorrencia });

// ---- cadastro de tarefas avulsas e recorrentes (modelos): a equipe escolhe numa lista em vez de escrever
export const TipoTarefaModelo = z.enum(['AVULSA', 'RECORRENTE']);
export type TipoTarefaModelo = z.infer<typeof TipoTarefaModelo>;
export const ItemChecklistModelo = z.object({ texto: z.string().trim().min(1).max(200), obrigatorio: z.boolean().default(false) });
export type ItemChecklistModelo = z.infer<typeof ItemChecklistModelo>;

export const TarefaModeloBody = z.object({
  /** vira o título da tarefa */
  nome: z.string().trim().min(2).max(180),
  tipo: TipoTarefaModelo.default('AVULSA'),
  departamentoSigla: z.string().trim().max(8).nullable().default(null),
  responsavelMembroId: Id.nullable().default(null),
  /** avulsa: vencimento sugerido = hoje + N dias corridos */
  prazoDias: z.number().int().min(0).max(3650).nullable().default(null),
  /** prazo interno: dias úteis antes do vencimento (os dois tipos) */
  antecedenciaDiasUteis: z.number().int().min(0).max(60).default(0),
  // só recorrente
  intervalo: z.number().int().min(1).max(60).default(1),
  unidade: UnidadeRecorrencia.default('MES'),
  ocorrencias: z.number().int().min(1).max(240).nullable().default(null),
  ajuste: AjusteNaoUtil.default('ANTECIPAR'),
  aberturaDiasAntes: z.number().int().min(0).max(365).default(10),
  observacao: z.string().trim().max(2000).nullable().default(null),
  checklist: z.array(ItemChecklistModelo).max(40).default([]),
  /** o que a tarefa criada por este modelo gera (ausente/nulo = não mexe) */
  entregaveis: Entregaveis.nullable().optional(),
  ordem: z.number().int().min(0).max(9999).default(0),
  ativo: z.boolean().default(true),
});
export type TarefaModeloBody = z.infer<typeof TarefaModeloBody>;
/** Edição: só o que vier no corpo é alterado (sem defaults). */
export const TarefaModeloEditarBody = z.object({
  nome: z.string().trim().min(2).max(180).optional(), tipo: TipoTarefaModelo.optional(), departamentoSigla: z.string().trim().max(8).nullable().optional(), responsavelMembroId: Id.nullable().optional(),
  prazoDias: z.number().int().min(0).max(3650).nullable().optional(), antecedenciaDiasUteis: z.number().int().min(0).max(60).optional(),
  intervalo: z.number().int().min(1).max(60).optional(), unidade: UnidadeRecorrencia.optional(), ocorrencias: z.number().int().min(1).max(240).nullable().optional(), ajuste: AjusteNaoUtil.optional(), aberturaDiasAntes: z.number().int().min(0).max(365).optional(),
  observacao: z.string().trim().max(2000).nullable().optional(), checklist: z.array(ItemChecklistModelo).max(40).optional(), ordem: z.number().int().min(0).max(9999).optional(), ativo: z.boolean().optional(),
  entregaveis: Entregaveis.nullable().optional(),
});
export type TarefaModeloEditarBody = z.infer<typeof TarefaModeloEditarBody>;
export const TarefaModelo = z.object({
  id: Id, nome: z.string(), tipo: TipoTarefaModelo, departamentoSigla: z.string().nullable(), responsavelMembroId: Id.nullable(), responsavelApelido: z.string().nullable(),
  prazoDias: z.number().int().nullable(), antecedenciaDiasUteis: z.number().int(),
  intervalo: z.number().int(), unidade: UnidadeRecorrencia, ocorrencias: z.number().int().nullable(), ajuste: AjusteNaoUtil, aberturaDiasAntes: z.number().int(),
  /** "todo ano", "a cada 3 meses"… (só recorrente) */
  rotuloRepeticao: z.string().nullable(),
  observacao: z.string().nullable(), checklist: z.array(ItemChecklistModelo), ordem: z.number().int(), ativo: z.boolean(),
  /** o que a tarefa criada por este modelo gera */
  entregaveis: Entregaveis.nullable().optional(),
});
export type TarefaModelo = z.infer<typeof TarefaModelo>;
export const TarefaModelosPadrao = z.object({ criados: z.number().int(), existentes: z.number().int(), /** tarefas que já existiam sem checklist e ganharam o sugerido */ checklists: z.number().int().default(0) });
